"use client";

import { c as _c } from "react/compiler-runtime";
import { cn } from "@/utils/cn";
import React from "react";
import { Input as AriaInput, Label, Text, TextField } from "react-aria-components";
import { useController, type Control, type FieldValues, type Path, type RegisterOptions } from "react-hook-form";
import { ArrayInput } from "../rac/array-input";
import { ButtonGroup } from "../rac/button-group";
import { CheckboxGroupInput } from "../rac/checkbox-group-input";
import { CurrencyField } from "../rac/currency-input";
import { JollyDatePicker, JollyDateRangePicker } from "../rac/date-picker";
import { RadioGroupInput } from "../rac/radio-group-input";
import { SelectInput } from "../rac/select-input";
import { SelectInput as SelectInput2 } from "../rac/select-input2";
import { SwitchInput } from "../rac/switch-input";
import { TextAreaInput } from "../rac/text-area-input";
import { normalizeDate, type InputDate, type InputRange } from "./normalize-date";
interface FallbackInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: React.ReactNode;
  name?: string;
  description?: React.ReactNode;
  errorMessage?: React.ReactNode;
  className?: string;
  inputClassName?: string;
  labelClassName?: string;
}
export const FallbackInput = React.forwardRef<HTMLInputElement, FallbackInputProps>((t0, ref) => {
  const $ = _c(34);
  let className;
  let description;
  let errorMessage;
  let inputClassName;
  let label;
  let labelClassName;
  let name;
  let props;
  if ($[0] !== t0) {
    ({
      label,
      name,
      description,
      errorMessage,
      className,
      inputClassName,
      labelClassName,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = description;
    $[3] = errorMessage;
    $[4] = inputClassName;
    $[5] = label;
    $[6] = labelClassName;
    $[7] = name;
    $[8] = props;
  } else {
    className = $[1];
    description = $[2];
    errorMessage = $[3];
    inputClassName = $[4];
    label = $[5];
    labelClassName = $[6];
    name = $[7];
    props = $[8];
  }
  let t1;
  if ($[9] !== className) {
    t1 = cn("flex flex-col gap-1", className);
    $[9] = className;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  let t2;
  if ($[11] !== label || $[12] !== labelClassName || $[13] !== name) {
    t2 = label && <Label htmlFor={name} className={cn("text-title-medium", labelClassName)}>{label}</Label>;
    $[11] = label;
    $[12] = labelClassName;
    $[13] = name;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  const t3 = props.value ?? "";
  const t4 = errorMessage ? "border-primary-600" : "border-gray-300";
  let t5;
  if ($[15] !== inputClassName || $[16] !== t4) {
    t5 = cn("border px-3 py-2.5 h-10 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-600", t4, inputClassName);
    $[15] = inputClassName;
    $[16] = t4;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== name || $[19] !== props || $[20] !== ref || $[21] !== t3 || $[22] !== t5) {
    t6 = <AriaInput id={name} ref={ref} {...props} value={t3} className={t5} />;
    $[18] = name;
    $[19] = props;
    $[20] = ref;
    $[21] = t3;
    $[22] = t5;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  let t7;
  if ($[24] !== description) {
    t7 = description && <Text slot="description" className="text-xs text-muted-foreground">{description}</Text>;
    $[24] = description;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] !== errorMessage) {
    t8 = errorMessage && <Text slot="errorMessage" className="text-sm font-medium text-destructive">{errorMessage}</Text>;
    $[26] = errorMessage;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  let t9;
  if ($[28] !== t1 || $[29] !== t2 || $[30] !== t6 || $[31] !== t7 || $[32] !== t8) {
    t9 = <TextField className={t1}>{t2}{t6}{t7}{t8}</TextField>;
    $[28] = t1;
    $[29] = t2;
    $[30] = t6;
    $[31] = t7;
    $[32] = t8;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  return t9;
});
FallbackInput.displayName = "FallbackInput";
interface ErrorBoundaryProps {
  children: React.ReactNode;
}
interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null
    };
  }
  static getDerivedStateFromError(error: Error) {
    return {
      hasError: true,
      error
    };
  }
  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    if (!errorInfo) return;
    if (error) throw error;
    // Log.fatal("ErrorBoundary caught an error", {
    // error: error.toString(),
    // errorInfo: errorInfo.componentStack,
    // });
  }
  render() {
    if (this.state.hasError && this.state.error) {
      return <h1>{this.state.error.toString()}</h1>;
    }
    return this.props.children;
  }
}
const racTypeToComponent: Record<string, React.ElementType> = {
  radio: RadioGroupInput,
  checkbox: CheckboxGroupInput,
  inr: CurrencyField,
  buttongroup: ButtonGroup,
  date: JollyDatePicker,
  daterange: JollyDateRangePicker,
  select: SelectInput,
  textarea: TextAreaInput,
  array: ArrayInput,
  switch: SwitchInput,
  select2: SelectInput2
};
const typeMap = new Map([["array", "array"], ["inr", "inr"], ["number", "number"], ["radio", "radio"], ["checkbox", "checkbox"], ["switch", "switch"], ["buttongroup", "buttongroup"], ["date", "date"], ["daterange", "daterange"], ["select", "select"], ["textarea", "textarea"], ["email", "email"], ["select", "select"], ["select2", "select2"]]);
const normalizeInputType = (type: string = "text"): string => {
  return typeMap.get(type.toLowerCase()) || type.toLowerCase() || "text";
};
type Option = {
  label: string;
  value: unknown;
} | {
  label: string;
  value: unknown;
  options: Option[];
};
interface InputProps<T extends FieldValues = FieldValues> {
  type?: string;
  name?: Path<T>;
  control?: Control<T>;
  rules?: RegisterOptions<T>;
  options?: Option[];
  markFieldDirty?: (fieldName: string) => void; // Hack for manual dirty tracking
  [key: string]: unknown;
}
export const Input = t0 => {
  const $ = _c(15);
  let control;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      type: t1,
      control,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = control;
    $[2] = props;
    $[3] = t1;
  } else {
    control = $[1];
    props = $[2];
    t1 = $[3];
  }
  const type = t1 === undefined ? "text" : t1;
  let t2;
  if ($[4] !== type) {
    t2 = normalizeInputType(type);
    $[4] = type;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const normalizedType = t2;
  const Component = racTypeToComponent[normalizedType] || FallbackInput;
  if (control) {
    let t3;
    if ($[6] !== Component || $[7] !== control || $[8] !== normalizedType || $[9] !== props) {
      t3 = <RHFInput type={normalizedType} control={control} Component={Component} {...props} />;
      $[6] = Component;
      $[7] = control;
      $[8] = normalizedType;
      $[9] = props;
      $[10] = t3;
    } else {
      t3 = $[10];
    }
    return t3;
  }
  let t3;
  if ($[11] !== Component || $[12] !== normalizedType || $[13] !== props) {
    t3 = <ErrorBoundary><Component type={normalizedType} {...props} /></ErrorBoundary>;
    $[11] = Component;
    $[12] = normalizedType;
    $[13] = props;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  return t3;
};
const normalizedValueFunc = (value: unknown, type: string): unknown => {
  if (type === "date" || type === "daterange") {
    return normalizeDate(value as InputDate | InputRange);
  }
  // For text-based inputs, ensure we never return null/undefined
  if (type === "text" || type === "email" || type === "textarea") {
    return value ?? "";
  }
  // For number inputs, ensure we return empty string instead of null/undefined
  if (type === "number") {
    return value ?? "";
  }
  return value ?? "";
};
export const parsedValueFunc = (e: unknown, type: string): unknown => {
  switch (type) {
    case "switch":
      return e;
    case "number":
      {
        const target = (e as {
          target: {
            value: string;
          };
        }).target;
        return target.value === "" ? undefined : Number(target.value);
      }
    case "text":
    case "email":
    case "checkbox":
    case "radio":
    case "inr":
    case "buttongroup":
    case "array":
    case "select":
    case "select2":
      return e;
    case "date":
    case "daterange":
      return normalizeDate(e as InputDate | InputRange);
    case "textarea":
      {
        const target = (e as {
          target?: {
            value: string;
          };
        }).target;
        return target?.value || e;
      }
    default:
      {
        const target = (e as {
          target: {
            value: string;
          };
        }).target;
        return target.value;
      }
  }
};
interface RHFInputProps<T extends FieldValues = FieldValues> {
  type: string;
  name?: Path<T>;
  control: Control<T>;
  rules?: RegisterOptions<T>;
  Component: React.ElementType;
  markFieldDirty?: (fieldName: string) => void; // Hack for manual dirty tracking
  [key: string]: unknown;
}
function RHFInput(t0) {
  const $ = _c(29);
  let Component;
  let control;
  let markFieldDirty;
  let name;
  let rest;
  let rules;
  let type;
  if ($[0] !== t0) {
    ({
      type,
      name,
      control,
      rules,
      Component,
      markFieldDirty,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = Component;
    $[2] = control;
    $[3] = markFieldDirty;
    $[4] = name;
    $[5] = rest;
    $[6] = rules;
    $[7] = type;
  } else {
    Component = $[1];
    control = $[2];
    markFieldDirty = $[3];
    name = $[4];
    rest = $[5];
    rules = $[6];
    type = $[7];
  }
  if (!name) {
    throw new Error("RHFInput requires a name prop when used with control");
  }
  const t1 = name as Path<T>;
  let t2;
  if ($[8] !== control || $[9] !== rules || $[10] !== t1) {
    t2 = {
      name: t1,
      control,
      rules
    };
    $[8] = control;
    $[9] = rules;
    $[10] = t1;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  const {
    field: t3,
    fieldState: t4
  } = useController(t2);
  const {
    onChange,
    onBlur,
    value
  } = t3;
  const {
    error
  } = t4;
  let t5;
  if ($[12] !== markFieldDirty || $[13] !== name || $[14] !== onChange || $[15] !== type || $[16] !== value) {
    t5 = e => {
      const parsedValue = parsedValueFunc(e, type);
      if (value !== parsedValue) {
        onChange(parsedValue);
        if (markFieldDirty && name) {
          markFieldDirty(name);
        }
      }
    };
    $[12] = markFieldDirty;
    $[13] = name;
    $[14] = onChange;
    $[15] = type;
    $[16] = value;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const handleChange = t5;
  let t6;
  if ($[18] !== type || $[19] !== value) {
    t6 = normalizedValueFunc(value, type);
    $[18] = type;
    $[19] = value;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  const normalizedValue = t6;
  const t7 = error?.message;
  let t8;
  if ($[21] !== Component || $[22] !== handleChange || $[23] !== normalizedValue || $[24] !== onBlur || $[25] !== rest || $[26] !== t7 || $[27] !== type) {
    t8 = <ErrorBoundary><Component type={type} value={normalizedValue} onChange={handleChange} onBlur={onBlur} errorMessage={t7} {...rest} /></ErrorBoundary>;
    $[21] = Component;
    $[22] = handleChange;
    $[23] = normalizedValue;
    $[24] = onBlur;
    $[25] = rest;
    $[26] = t7;
    $[27] = type;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  return t8;
}